import React, { useState, useMemo } from "react";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import api, { formatVND } from "@/lib/api";
import { toast } from "sonner";
import { Search, ChevronDown, ChevronUp, Phone, Clock, Pencil, Trash2, CheckCircle2, XCircle, ShieldCheck, AlertTriangle, Image as ImageIcon, Volleyball, Target, Car, Swords, BadgeCheck, CircleDollarSign, User } from "lucide-react";

const STATUS_MAP = {
  confirmed: { label: "Đã xác nhận", className: "bg-lime-500/20 text-lime-400 border-lime-500/30" },
  pending: { label: "Chờ xác nhận", className: "bg-amber-500/20 text-amber-400 border-amber-500/30" },
  completed: { label: "Hoàn thành", className: "bg-sky-500/20 text-sky-400 border-sky-500/30" },
  cancelled: { label: "Đã huỷ", className: "bg-red-500/20 text-red-400 border-red-500/30" },
};

const FILTER_BUTTONS = [
  { value: "", label: "Tất cả" },
  { value: "confirmed", label: "Đã xác nhận" },
  { value: "pending", label: "Chờ xác nhận" },
  { value: "completed", label: "Hoàn thành" },
  { value: "cancelled", label: "Đã huỷ" },
];

export default function BookingList({ bookings, onEdit, onDelete, onApprove, onCancel, onRefresh }) {
  const [expanded, setExpanded] = useState(true);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [viewReceiptUrl, setViewReceiptUrl] = useState(null);
  const [verifyingId, setVerifyingId] = useState(null);

  const handleVerifyPayment = async (bookingId) => {
    setVerifyingId(bookingId);
    try {
      await api.post(`/bookings/${bookingId}/verify-payment`);
      toast.success("Đã xác nhận thanh toán");
      if (onRefresh) onRefresh();
    } catch (err) { toast.error(err.response?.data?.detail || "Lỗi"); }
    finally { setVerifyingId(null); }
  };

  const handleUnverifyPayment = async (bookingId) => {
    setVerifyingId(bookingId);
    try {
      await api.post(`/bookings/${bookingId}/unverify-payment`);
      toast.success("Đã hoàn tác xác nhận thanh toán");
      if (onRefresh) onRefresh();
    } catch (err) { toast.error(err.response?.data?.detail || "Lỗi"); }
    finally { setVerifyingId(null); }
  };

  const filtered = useMemo(() => {
    let list = [...bookings]
      // Filter out auto-generated bookings from groups (hội)
      .filter(b => !(b.notes || "").includes("Booking tự động từ hội"))
      // Filter out completed bookings
      .filter(b => b.status !== "completed")
      .sort((a, b) => {
        // Newest (highest ID / latest created) first
        const da = a.created_at ? new Date(a.created_at) : 0;
        const db = b.created_at ? new Date(b.created_at) : 0;
        return db - da;
      });
    // Social bookings on top
    list.sort((a, b) => {
      const aS = a.booking_type === "social" ? 1 : 0;
      const bS = b.booking_type === "social" ? 1 : 0;
      return bS - aS;
    });
    if (statusFilter) list = list.filter((b) => b.status === statusFilter);
    if (search) {
      const q = search.toLowerCase();
      list = list.filter(
        (b) =>
          (b.customer_name || "").toLowerCase().includes(q) ||
          (b.customer_phone || "").includes(q) ||
          (b.organizer_name || "").toLowerCase().includes(q)
      );
    }
    return list;
  }, [bookings, statusFilter, search]);

  return (
    <div className="bg-[#1e293b] border border-[#334155] rounded-lg overflow-hidden" data-testid="booking-list-panel">
      {/* Toggle header */}
      <button
        onClick={() => setExpanded(!expanded)}
        className="w-full flex items-center justify-between px-4 py-3 hover:bg-[#334155]/30 transition-colors"
        data-testid="toggle-booking-list"
      >
        <span className="text-sm font-semibold text-[#F8FAFC] font-heading">
          Danh sách Booking ({filtered.length})
        </span>
        {expanded ? <ChevronUp className="w-4 h-4 text-slate-400" /> : <ChevronDown className="w-4 h-4 text-slate-400" />}
      </button>

      {expanded && (
        <div className="px-4 pb-4">
          {/* Search + filters */}
          <div className="flex flex-col sm:flex-row gap-3 mb-4">
            <div className="flex items-center gap-2 bg-[#0f172a] border border-[#334155] rounded-lg px-3 flex-1">
              <Search className="w-4 h-4 text-slate-500 shrink-0" />
              <Input
                placeholder="Tìm khách hàng, SĐT..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="border-0 bg-transparent text-slate-200 placeholder:text-slate-500 focus-visible:ring-0 focus-visible:ring-offset-0 h-9"
                data-testid="booking-list-search"
              />
            </div>
            <div className="flex gap-1.5 flex-wrap">
              {FILTER_BUTTONS.map((f) => (
                <button
                  key={f.value}
                  onClick={() => setStatusFilter(f.value)}
                  className={`px-2.5 py-1 rounded-md text-xs font-medium transition-colors ${
                    statusFilter === f.value
                      ? "bg-lime-500/20 text-lime-400"
                      : "bg-[#0f172a] text-slate-400 hover:text-slate-200"
                  }`}
                  data-testid={`filter-${f.value || "all"}`}
                >
                  {f.label}
                </button>
              ))}
            </div>
          </div>

          {/* Cards grid */}
          {filtered.length === 0 ? (
            <div className="text-center py-8 text-slate-500 text-sm">Không tìm thấy đặt sân</div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
              {filtered.map((b, idx) => {
                const st = STATUS_MAP[b.status] || STATUS_MAP.pending;
                const isSocial = b.booking_type === "social";
                const durationH = ((parseInt(b.end_time.split(":")[0]) * 60 + parseInt(b.end_time.split(":")[1])) -
                  (parseInt(b.start_time.split(":")[0]) * 60 + parseInt(b.start_time.split(":")[1]))) / 60;
                const createdTime = b.created_at ? new Date(b.created_at).toLocaleTimeString("vi-VN", { hour: "2-digit", minute: "2-digit" }) : "";

                return (
                  <div
                    key={b.id}
                    className={`bg-[#0f172a] border rounded-lg p-3 transition-all ${isSocial ? "border-teal-500/40 bg-teal-500/5 hover:border-teal-400/60" : "border-[#334155] hover:border-lime-500/20"} ${b.status === "pending" ? "animate-pending-pulse" : ""}`}
                    data-testid={`booking-card-${b.id}`}
                  >
                    {/* Header */}
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-bold text-slate-300">#{filtered.length - idx}</span>
                        <span className="text-[10px] text-slate-500">({createdTime})</span>
                        {isSocial && (
                          <Badge className="bg-teal-500/20 text-teal-400 border-teal-500/30 text-[10px] px-1.5 py-0">SOCIAL</Badge>
                        )}
                      </div>
                      <Badge className={`${st.className} text-[10px] rounded-full border px-1.5 py-0`}>
                        {st.label}
                      </Badge>
                    </div>

                    {/* Name */}
                    <p className="text-sm font-semibold text-[#F8FAFC] truncate">
                      {b.customer_name || b.organizer_name}
                    </p>

                    {/* Phone */}
                    <div className="flex items-center gap-1.5 mt-1 text-xs text-slate-400">
                      <Phone className="w-3 h-3" />
                      <span>{b.customer_phone || "—"}</span>
                    </div>

                    {/* Court + time */}
                    <div className="flex items-center gap-1.5 mt-1 text-xs text-slate-400">
                      <Clock className="w-3 h-3" />
                      <span>{b.court_name} · {b.start_time}-{b.end_time} · {durationH}h</span>
                    </div>

                    {/* Booked at time */}
                    {b.created_at && (
                      <div className="text-[10px] text-slate-600 mt-0.5">
                        Đặt lúc: {(() => { const d = new Date(b.created_at); return `${String(d.getHours()).padStart(2,"0")}:${String(d.getMinutes()).padStart(2,"0")} ${String(d.getDate()).padStart(2,"0")}/${String(d.getMonth()+1).padStart(2,"0")}`; })()}
                      </div>
                    )}

                    {/* Đặt hộ bởi: staff/ceo (ẩn nếu do chính customer đặt) */}
                    {b.created_by_name && b.created_by_role && b.created_by_role !== "customer" && (
                      <div className="text-[10px] text-amber-300 mt-0.5 flex items-center gap-1" data-testid={`booked-by-staff-${b.id}`}>
                        <User className="w-3 h-3" /> Đặt bởi: <span className="font-semibold">{b.created_by_name}</span>
                        <span className="text-slate-500">({b.created_by_role})</span>
                      </div>
                    )}

                    {/* Payment status */}
                    {b.payment_confirmed_at && (
                      <div className="mt-1.5">
                        {b.payment_verified_at ? (
                          <div className="flex items-center gap-1.5">
                            <span className="inline-flex items-center gap-1 text-[10px] bg-emerald-500/10 text-emerald-400 border border-emerald-500/20 rounded-full px-2 py-0.5 font-medium">
                              <BadgeCheck className="w-3 h-3" /> Đã XN thanh toán
                              {b.payment_verified_by && <span className="text-emerald-500/60">({b.payment_verified_by})</span>}
                            </span>
                            <button onClick={() => handleUnverifyPayment(b.id)} disabled={verifyingId === b.id}
                              className="text-[10px] text-slate-500 hover:text-red-400 font-medium transition-colors disabled:opacity-50"
                              data-testid={`unverify-payment-${b.id}`}>
                              Hoàn tác
                            </button>
                          </div>
                        ) : (
                          <div className="flex items-center gap-1.5">
                            <span className="inline-flex items-center gap-1 text-[10px] bg-amber-500/10 text-amber-400 border border-amber-500/20 rounded-full px-2 py-0.5 font-medium">
                              <CircleDollarSign className="w-3 h-3" /> Chờ XN thanh toán
                            </span>
                            <button onClick={() => handleVerifyPayment(b.id)} disabled={verifyingId === b.id}
                              className="text-[10px] bg-emerald-500 hover:bg-emerald-600 text-white font-semibold px-2 py-0.5 rounded-full transition-colors disabled:opacity-50"
                              data-testid={`verify-payment-${b.id}`}>
                              {verifyingId === b.id ? "..." : "XN thanh toán"}
                            </button>
                          </div>
                        )}
                      </div>
                    )}

                    {/* Price */}
                    <div className="flex items-center justify-between mt-2 pt-2 border-t border-[#334155]">
                      <div className="text-xs">
                        <span className="text-lime-400 font-semibold">{formatVND(b.total_price)}đ</span>
                        {b.deposit > 0 && (
                          <span className="text-slate-500 ml-2">Cọc: {formatVND(b.deposit)}đ</span>
                        )}
                      </div>
                      <div className="flex gap-1 items-center">
                        {/* AI Verified icon */}
                        {b.ai_verified === true && (
                          <span className="p-1 text-emerald-400" title="AI đã xác thực - Số tiền khớp">
                            <ShieldCheck className="w-4 h-4" />
                          </span>
                        )}
                        {b.ai_receipt_amount != null && b.ai_receipt_amount > 0 && b.ai_verified === false && (
                          <span className="p-1 text-amber-400" title={`AI cảnh báo chênh lệch: ${formatVND(b.ai_receipt_amount)}đ`}>
                            <AlertTriangle className="w-4 h-4" />
                          </span>
                        )}
                        {b.status === "pending" && onApprove && (
                          <button
                            onClick={() => onApprove(b.id)}
                            className="p-1 rounded hover:bg-lime-500/10 text-amber-400 hover:text-lime-400 transition-colors"
                            title="Xác nhận đặt sân"
                            data-testid={`approve-booking-${b.id}`}
                          >
                            <CheckCircle2 className="w-3.5 h-3.5" />
                          </button>
                        )}
                        {(b.status === "pending" || b.status === "confirmed") && onCancel && (
                          <button
                            onClick={() => onCancel(b.id)}
                            className="p-1 rounded hover:bg-red-500/10 text-slate-500 hover:text-red-400 transition-colors"
                            title="Huỷ đặt sân"
                            data-testid={`cancel-booking-${b.id}`}
                          >
                            <XCircle className="w-3.5 h-3.5" />
                          </button>
                        )}
                        <button
                          onClick={() => onEdit(b)}
                          className="p-1 rounded hover:bg-[#334155] text-slate-400 hover:text-lime-400 transition-colors"
                          data-testid={`edit-booking-${b.id}`}
                        >
                          <Pencil className="w-3.5 h-3.5" />
                        </button>
                        <button
                          onClick={() => onDelete(b.id)}
                          className="p-1 rounded hover:bg-red-500/10 text-slate-400 hover:text-red-400 transition-colors"
                          data-testid={`delete-booking-card-${b.id}`}
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    </div>

                    {/* AI Receipt Analysis */}
                    {(b.receipt_image_url || b.ai_receipt_amount > 0) && (
                      <div className={`mt-2 pt-2 border-t border-[#334155] text-[10px] space-y-1 ${b.ai_verified ? "bg-emerald-500/5 -mx-3 -mb-3 px-3 pb-2 rounded-b-lg" : b.ai_receipt_amount > 0 ? "bg-amber-500/5 -mx-3 -mb-3 px-3 pb-2 rounded-b-lg" : ""}`}>
                        {b.ai_verified === true && (
                          <div className="flex items-center gap-1.5 text-emerald-400 font-semibold">
                            <ShieldCheck className="w-3 h-3" /> AI đã xác thực
                          </div>
                        )}
                        {b.ai_receipt_amount != null && b.ai_receipt_amount > 0 && b.ai_verified === false && (
                          <div className="flex items-center gap-1.5 text-amber-400 font-semibold">
                            <AlertTriangle className="w-3 h-3" /> AI cảnh báo — cần kiểm tra
                          </div>
                        )}
                        {b.ai_receipt_amount > 0 && (
                          <p className="text-slate-400">Số tiền: <span className={Math.abs((b.ai_receipt_amount || 0) - (b.total_price || 0)) <= 5000 ? "text-emerald-400 font-semibold" : "text-amber-400 font-semibold"}>{formatVND(b.ai_receipt_amount)}đ</span>
                            {Math.abs((b.ai_receipt_amount || 0) - (b.total_price || 0)) > 5000 && <span className="text-red-400 ml-1">(chênh lệch)</span>}
                          </p>
                        )}
                        {b.ai_recipient_name && (
                          <p className="text-slate-400">Người nhận: <span className={b.ai_recipient_valid ? "text-emerald-400 font-semibold" : "text-red-400 font-semibold"}>{b.ai_recipient_name}</span>
                            {!b.ai_recipient_valid && <span className="text-red-400 ml-1">(không khớp)</span>}
                          </p>
                        )}
                        {b.ai_receipt_time && (
                          <p className="text-slate-400">Thời gian GD: <span className={b.ai_time_valid ? "text-emerald-400" : "text-amber-400"}>{b.ai_receipt_time}</span>
                            {b.ai_time_ago && <span className="text-slate-500 ml-1">({b.ai_time_ago})</span>}
                            {b.ai_time_valid === false && b.ai_receipt_time && <span className="text-red-400 ml-1">(không phải hôm nay)</span>}
                          </p>
                        )}
                        {b.receipt_image_url && (
                          <button
                            onClick={() => setViewReceiptUrl(b.receipt_image_url)}
                            className="flex items-center gap-1 text-sky-400 hover:text-sky-300 transition-colors mt-1"
                            data-testid={`view-receipt-${b.id}`}
                          >
                            <ImageIcon className="w-3 h-3" /> Xem biên nhận
                          </button>
                        )}
                      </div>
                    )}

                    {/* Add-on requests */}
                    {(b.addon_ball_picker || b.addon_ball_machine || b.addon_training_cart || b.addon_racket_count > 0) && (
                      <div className="mt-1.5 flex flex-wrap gap-1">
                        {b.addon_ball_picker && <span className="text-[10px] bg-teal-500/10 text-teal-400 border border-teal-500/20 rounded px-1.5 py-0.5">Nhặt bóng</span>}
                        {b.addon_ball_machine && <span className="text-[10px] bg-sky-500/10 text-sky-400 border border-sky-500/20 rounded px-1.5 py-0.5">Máy bắn bóng</span>}
                        {b.addon_training_cart && <span className="text-[10px] bg-amber-500/10 text-amber-400 border border-amber-500/20 rounded px-1.5 py-0.5">Xe tập bóng</span>}
                        {b.addon_racket_count > 0 && <span className="text-[10px] bg-purple-500/10 text-purple-400 border border-purple-500/20 rounded px-1.5 py-0.5">Vợt: {b.addon_racket_count} cái</span>}
                      </div>
                    )}

                    {/* Notes */}
                    {b.notes && (
                      <div className="mt-1.5 bg-amber-500/5 border border-amber-500/15 rounded-md px-2.5 py-1.5">
                        <p className="text-xs text-amber-200/90 leading-relaxed whitespace-pre-wrap">{b.notes}</p>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* Receipt image viewer */}
      {viewReceiptUrl && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center p-4" style={{ zIndex: 9999 }}
          onClick={() => setViewReceiptUrl(null)} data-testid="receipt-viewer-overlay">
          <div className="relative max-w-lg max-h-[80vh]" onClick={(e) => e.stopPropagation()}>
            <img src={viewReceiptUrl} alt="Biên nhận" className="max-w-full max-h-[80vh] rounded-lg object-contain border border-[#334155]" />
            <button onClick={() => setViewReceiptUrl(null)}
              className="absolute -top-3 -right-3 w-8 h-8 rounded-full bg-red-500 hover:bg-red-600 text-white flex items-center justify-center shadow-lg transition-colors"
              data-testid="close-receipt-viewer">
              <XCircle className="w-5 h-5" />
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
